<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { accountCurrency, formatMoney } from 'dashboard/helper/currency';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  experience: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete']);

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();
const toggling = ref(false);

const isActive = computed(() => props.experience.active);

const formatPrice = amount => {
  const formatted = formatMoney(amount, accountCurrency(currentAccount.value));
  return formatted || '—';
};

const toggleActive = async () => {
  toggling.value = true;
  try {
    await store.dispatch('planningExperiences/update', {
      id: props.experience.id,
      active: !isActive.value,
    });
  } catch {
    useAlert(t('PLANNING_EXPERIENCES.FORM.API.ERROR_MESSAGE'));
  } finally {
    toggling.value = false;
  }
};
</script>

<template>
  <tr class="">
    <td class="py-4 ltr:pr-4 rtl:pl-4">
      <div class="flex items-start gap-6">
        <img
          v-if="experience.cover_image_url"
          :src="experience.cover_image_url"
          class="w-16 h-16 rounded-lg object-cover shrink-0"
        />
        <div
          v-else
          class="w-16 h-16 rounded-lg bg-n-alpha-2 flex items-center justify-center shrink-0"
        >
          <Icon icon="i-lucide-sparkles" class="size-4 text-n-slate-10" />
        </div>
        <div class="flex flex-col min-w-0 min-h-16 justify-center">
          <p class="font-medium text-n-slate-12 truncate">
            {{ experience.name }}
          </p>
          <p v-if="experience.description" class="text-sm text-n-slate-11">
            {{ experience.description }}
          </p>
        </div>
      </div>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4">
      <span class="text-n-slate-11 whitespace-nowrap">
        <template v-if="experience.pricing_mode === 'dynamic'">
          {{
            t('PLANNING_EXPERIENCES.LIST.FROM_PRICE', {
              price: formatPrice(experience.starting_from_price),
            })
          }}
        </template>
        <template v-else>
          {{ formatPrice(experience.starting_from_price) }}
        </template>
      </span>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4" @click.stop>
      <Switch
        :model-value="isActive"
        :disabled="toggling"
        @change="toggleActive"
      />
    </td>
    <td class="py-4" @click.stop>
      <div class="flex items-center justify-end gap-1">
        <Button
          v-tooltip.top="$t('SETTINGS.SPACES.EDIT')"
          icon="i-lucide-pen-line"
          slate
          md
          faded
          :is-loading="loading"
          @click="$emit('edit', experience)"
        />
        <Button
          v-tooltip.top="$t('PLANNING_EXPERIENCES.LIST.DELETE')"
          :is-loading="loading"
          icon="i-lucide-trash-2"
          md
          ruby
          faded
          @click="$emit('delete', experience)"
        />
      </div>
    </td>
  </tr>
</template>
